import { Component, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'tmw-chips-input',
  standalone: true,
  imports: [CommonModule, FormsModule], // Import CommonModule for *ngFor and FormsModule for [(ngModel)]
  templateUrl: './chips-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ChipsInputComponent),
      multi: true
    }
  ]
})
export class ChipsInputComponent implements ControlValueAccessor {

  // Internal state to hold the tags as an array
  tags: string[] = [];
  
  // Value of the new tag input field
  newTagValue = '';
  
  // Disabled state
  disabled = false;

  // --- CVA Boilerplate ---
  
  // Function to call when the internal value changes
  private onChange: (value: string | null) => void = () => {};
  
  // Function to call when the component is "touched"
  private onTouched: () => void = () => {};

  /**
   * Receives the model value from the form.
   * Parses "#tag1##tag2#" into ['tag1', 'tag2']
   */
  writeValue(value: string | null): void {
    if (typeof value === 'string' && value.trim().length > 0) {
      // Split by '#' and filter out any empty strings
      this.tags = value.split('#')
                        .filter(tag => tag.trim().length > 0);
    } else {
      this.tags = [];
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // --- Component-Specific Logic ---

  /**
   * Adds the tag from the input field.
   */
  addTag(): void {
    const tag = this.newTagValue.trim();

    // Only add if the tag is not empty and doesn't already exist
    if (tag.length > 0 && !this.tags.includes(tag)) {
      this.tags.push(tag);
      this.updateModel();
    }
    
    // Clear the input
    this.newTagValue = '';
    this.onTouched(); // Mark as touched when a tag is added
  }

  /**
   * Removes a tag by its index.
   */
  removeTag(index: number): void {
    if (index >= 0 && index < this.tags.length) {
      this.tags.splice(index, 1);
      this.updateModel();
      this.onTouched(); // Mark as touched when a tag is removed
    }
  }

  /**
   * Called when the input field is blurred.
   */
  handleBlur(): void {
    this.onTouched();
  }

  /**
   * Updates the form model with the sorted, formatted string.
   */
  private updateModel(): void {
    if (this.tags.length === 0) {
      this.onChange(null); // Emit null for an empty state
      return;
    }

    // 1. Sort the tags alphabetically
    const sortedTags = [...this.tags].sort();
    
    // 2. Format the string as "#tag1##tag2#"
    const modelValue = `#${sortedTags.join('##')}#`;
    
    // 3. Emit the change
    this.onChange(modelValue);
  }
}